import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { AdsProviderType, ReadAdsCampaign } from '@tinct/core-api-models';
import { BadgeStatus, Button, LongText } from '@tinct/web-components';
import { CollectionColumnHeader } from '@tinct/web-collection';
import { formatRelativeTime } from '@tinct/core-utils';

export type UseAdsCampaignsColumnsOptions = {
  /** Workspace prefix for campaign links, e.g. `/w/<orgId>`. */
  basePath: string;
  /**
   * Which provider the rows come from. Google Ads (default) shows the Google
   * id; LinkedIn Ads shows the LinkedIn id (the `?tinct_ad=` value) and the
   * campaign type, since LinkedIn has no keywords.
   */
  provider?: AdsProviderType;
};

const PROVIDER_LABELS: Record<AdsProviderType, string> = {
  google_ads: 'Google Ads',
  linkedin_ads: 'LinkedIn Ads',
};

/** LinkedIn campaign types (`advertising_channel_type`), humanised. */
const LINKEDIN_CHANNEL_TYPE_LABELS: Record<string, string> = {
  SPONSORED_UPDATES: 'Sponsored content',
  TEXT_AD: 'Text ad',
  SPONSORED_INMAILS: 'Message ad',
  DYNAMIC: 'Dynamic ad',
};

export function useAdsCampaignsColumns(
  { basePath, provider = 'google_ads' }: UseAdsCampaignsColumnsOptions,
): ColumnDef<ReadAdsCampaign>[] {
  const providerLabel = PROVIDER_LABELS[provider];
  const isLinkedin = provider === 'linkedin_ads';

  const channelTypeColumn: ColumnDef<ReadAdsCampaign> = {
    accessorKey: 'advertisingChannelType',
    header: () => <span>Type</span>,
    enableSorting: false,
    cell: ({ row }) => {
      const type = row.original.advertisingChannelType;
      if (!type) return <span className="text-muted-foreground">-</span>;
      return (
        <span className="text-muted-foreground">
          {LINKEDIN_CHANNEL_TYPE_LABELS[type] ?? type.toLowerCase().replace(/_/g, ' ')}
        </span>
      );
    },
  };

  return [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Ads campaign" />
      ),
      cell: ({ row }) => (
        <LongText className="max-w-[240px] font-semibold">
          {row.original.name ?? '-'}
        </LongText>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'externalCampaignId',
      header: () => <span>{isLinkedin ? 'LinkedIn id' : 'Google id'}</span>,
      cell: ({ row }) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.externalCampaignId ?? '-'}
        </span>
      ),
      enableSorting: false,
    },
    // No keywords column: LinkedIn has no keyword targeting, `keywords` is
    // always empty — the campaign type is the useful axis instead.
    ...(isLinkedin ? [channelTypeColumn] : []),
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        if (!status) return <span className="text-muted-foreground">-</span>;
        // Google reports ENABLED, LinkedIn ACTIVE — both mean "serving".
        const serving = ['enabled', 'active'].includes(status.toLowerCase());
        return (
          <BadgeStatus variant={serving ? 'green' : 'yellow'}>
            {status.toLowerCase()}
          </BadgeStatus>
        );
      },
      filterFn: 'equals',
    },
    {
      id: 'matchedCampaign',
      header: () => <span>Matched campaign</span>,
      enableSorting: false,
      cell: ({ row }) => {
        // A row with no matchedCampaignId is synced-but-unmatched: no ADWORD
        // campaign targets its final URL yet.
        const matchedCampaignId = row.original.matchedCampaignId;
        if (!matchedCampaignId) {
          const finalUrl = row.original.finalUrls?.[0];
          return (
            <LongText className="max-w-[240px] text-xs text-muted-foreground">
              {finalUrl
                ? `Create an ads campaign (${providerLabel}) on ${finalUrl} to get variants`
                : `No ads campaign (${providerLabel}) targets this URL yet`}
            </LongText>
          );
        }
        return (
          <Button variant="link" className="h-auto p-0" asChild>
            <Link to={`${basePath}/campaigns/${matchedCampaignId}`}>
              View campaign
            </Link>
          </Button>
        );
      },
    },
    {
      accessorKey: 'matchNormalizedUrl',
      header: () => <span>Matched URL</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const url = row.original.matchNormalizedUrl;
        if (!url) return <span className="text-muted-foreground">-</span>;
        return (
          <LongText className="max-w-[220px] text-muted-foreground">
            {url}
          </LongText>
        );
      },
    },
    {
      accessorKey: 'lastSyncedAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Synced" />
      ),
      cell: ({ row }) => {
        const lastSyncedAt = row.original.lastSyncedAt;
        if (!lastSyncedAt) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="text-muted-foreground">
            {formatRelativeTime(new Date(lastSyncedAt))}
          </span>
        );
      },
    },
  ];
}
